import { useTheme } from '@emotion/react';
import { useSetAtom } from 'jotai';
import { useResetAtom } from 'jotai/utils';
import { useRouter } from 'next/router';
import React, { useCallback } from 'react';

import { Flex, Icon, Text } from '~/components/blocks';
import { NotificationListItem } from '~/components/partials/NotificationListItem';
import { OrganizationNotificationFragment } from '~/graphql/generated/graphql';
import { notificationPopoverState } from '~/state/layouts/SharedAppShell/atoms';
import { profileDrawerState } from '~/state/partials/patient_profile_drawer/atoms';
import {
  checkinEntryProfileDrawerState,
  draftAppointmentPatientProfileDrawerState,
  lineVisitorProfileDrawerState,
  outpatientQuestionnaireEntryProfileDrawerState,
  webVisitorProfileDrawerState,
} from '~/state/reception/atoms';
import { Label } from '~/utils/label';

type Props = {
  notification: OrganizationNotificationFragment;
};

export const OrganizationNotification = (props: Props) => {
  const { notification } = props;
  const router = useRouter();
  const theme = useTheme();
  const setProfileDrawerState = useSetAtom(profileDrawerState);
  const setLineVisitorProfileDrawerState = useSetAtom(lineVisitorProfileDrawerState);
  const setWebVisitorProfileDrawerState = useSetAtom(webVisitorProfileDrawerState);
  const setDraftAppointmentPatientProfileDrawerState = useSetAtom(
    draftAppointmentPatientProfileDrawerState,
  );
  const resetProfileDrawerState = useResetAtom(profileDrawerState);
  const resetLineVisitorProfileDrawerState = useResetAtom(lineVisitorProfileDrawerState);
  const resetWebVisitorProfileDrawerState = useResetAtom(webVisitorProfileDrawerState);
  const resetCheckinEntryProfileDrawerState = useResetAtom(checkinEntryProfileDrawerState);
  const resetOutpatientQuestionnaireEntryProfileDrawerState = useResetAtom(
    outpatientQuestionnaireEntryProfileDrawerState,
  );
  const resetDraftAppointmentPatientProfileDrawerState = useResetAtom(
    draftAppointmentPatientProfileDrawerState,
  );

  const setNotificationPopoverState = useSetAtom(notificationPopoverState);
  const handleOpenReception = useCallback(
    async (e: React.MouseEvent<HTMLLIElement>) => {
      // 受付ドロアーで既読処理をするため、親要素で既読処理が実行されないようにする
      e.stopPropagation();

      if (router.pathname !== '/reception') {
        await router.push('/reception');
      }

      // 受付通知パネルを閉じる
      setNotificationPopoverState((_state) => ({ ..._state, isOpen: false }));

      if (notification.appointment?.patient) {
        setProfileDrawerState({
          isOpen: true,
          appointmentId: notification.appointment.id,
          patientId: notification.appointment.patient.id,
          error: null,
        });
        resetWebVisitorProfileDrawerState();
        resetLineVisitorProfileDrawerState();
        resetCheckinEntryProfileDrawerState();
        resetOutpatientQuestionnaireEntryProfileDrawerState();
        resetDraftAppointmentPatientProfileDrawerState();
      }

      if (notification.appointment?.webVisitor) {
        setWebVisitorProfileDrawerState({
          isOpen: true,
          appointmentId: notification.appointment.id,
          webVisitorId: notification.appointment.webVisitor.id,
        });
        resetProfileDrawerState();
        resetLineVisitorProfileDrawerState();
        resetCheckinEntryProfileDrawerState();
        resetOutpatientQuestionnaireEntryProfileDrawerState();
        resetDraftAppointmentPatientProfileDrawerState();
      }

      if (notification.appointment?.lineVisitor) {
        setLineVisitorProfileDrawerState({
          isOpen: true,
          appointmentId: notification.appointment.id,
          lineVisitorId: notification.appointment.lineVisitor.id,
        });
        resetProfileDrawerState();
        resetWebVisitorProfileDrawerState();
        resetCheckinEntryProfileDrawerState();
        resetOutpatientQuestionnaireEntryProfileDrawerState();
        resetDraftAppointmentPatientProfileDrawerState();
      }

      if (notification.draftAppointment) {
        const patientId = notification.draftAppointment.patient.id;
        const draftAppointment = notification.draftAppointment;
        const appointmentId = draftAppointment.appointment?.id;

        // 本予約済みの場合は、本予約のドロアーを表示する
        if (appointmentId) {
          setProfileDrawerState({
            isOpen: true,
            appointmentId,
            patientId,
            error: null,
          });
          resetDraftAppointmentPatientProfileDrawerState();
        } else {
          setDraftAppointmentPatientProfileDrawerState({
            isOpen: true,
            draftAppointmentId: draftAppointment.id,
            patientId,
            error: null,
          });
          resetProfileDrawerState();
        }

        resetLineVisitorProfileDrawerState();
        resetWebVisitorProfileDrawerState();
        resetCheckinEntryProfileDrawerState();
        resetOutpatientQuestionnaireEntryProfileDrawerState();
      }
    },
    [
      notification.appointment?.id,
      notification.appointment?.patient,
      notification.appointment?.webVisitor,
      notification.appointment?.lineVisitor,
      notification.draftAppointment,
      router,
      setNotificationPopoverState,
      setProfileDrawerState,
      setLineVisitorProfileDrawerState,
      setWebVisitorProfileDrawerState,
      setDraftAppointmentPatientProfileDrawerState,
      resetProfileDrawerState,
      resetLineVisitorProfileDrawerState,
      resetWebVisitorProfileDrawerState,
      resetCheckinEntryProfileDrawerState,
      resetOutpatientQuestionnaireEntryProfileDrawerState,
      resetDraftAppointmentPatientProfileDrawerState,
    ],
  );
  const isTaskUncompleted = !notification.isRead || notification.appointment?.status === 'pending';

  return (
    <NotificationListItem onClick={handleOpenReception} isMarked={isTaskUncompleted}>
      <Flex flexDirection="column" marginRight={theme.space.m}>
        <Text fontWeight="bold" size="s">
          {notification.subject}
        </Text>
        <Text size="xs" color="grey01" marginTop={theme.space.xs}>
          {`${Label.YYYYMMDDja(notification.updatedAt)} ${Label.HHMM(notification.updatedAt)}`}
        </Text>
      </Flex>
      <Icon icon="forward-line" size="m" marginLeft="auto" />
    </NotificationListItem>
  );
};
